import React, { useState, useEffect } from "react"
import { Line, LineChart, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts"
import { computeCredentialOverview } from "../../../utils/credentialOverviewHelpers"

const StatCard = ({ label, value, tone }) => (
  <div className="bg-background border border-border rounded-lg p-4">
    <p className="text-xs text-muted-foreground mb-1">{label}</p>
    <p className={`text-2xl font-bold ${tone || "text-foreground"}`}>{value?.toLocaleString()}</p>
  </div>
)

const MiniLineChart = ({ data, color }) => (
  <div className="h-40">
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
        <CartesianGrid strokeDasharray="4 8" stroke="var(--color-border)" vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
        <Tooltip
          contentStyle={{ background: "var(--color-card)", border: "1px solid var(--color-border)", borderRadius: 8, fontSize: 12 }}
        />
        <Line type="monotone" dataKey="count" stroke={color} strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} />
      </LineChart>
    </ResponsiveContainer>
  </div>
)

const CredentialOverviewSection = ({ orgId }) => {
  const [range, setRange] = useState("30d")
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!orgId) return
    setLoading(true)
    computeCredentialOverview(orgId, range).then((result) => {
      setData(result)
      setLoading(false)
    })
  }, [orgId, range])

  if (loading || !data) {
    return <div className="h-96 bg-muted rounded-lg animate-pulse mb-6" />
  }

  return (
    <div className="bg-card border border-border rounded-lg p-5 mb-6">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-semibold text-foreground">Credential Activity</h3>
        <select
          value={range}
          onChange={(e) => setRange(e.target.value)}
          className="text-sm bg-background border border-border rounded-md px-2.5 py-1.5 text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
        >
          <option value="30d">30 days</option>
          <option value="90d">90 days</option>
          <option value="12m">12 months</option>
        </select>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <StatCard label="Credentials Issued" value={data?.issued} />
        <StatCard label="Verified" value={data?.verified} tone="text-success" />
        <StatCard label="Pending" value={data?.pending} tone="text-warning" />
        <StatCard label="Revoked" value={data?.revoked} tone={data?.revoked > 0 ? "text-destructive" : undefined} />
      </div>

      <div className="mb-6">
        <p className="text-sm font-medium text-foreground mb-3">Credentials Issued Over Time</p>
        <MiniLineChart data={data?.issuedOverTime} color="var(--color-primary)" />
      </div>

      <div>
        <p className="text-sm font-medium text-foreground mb-3">Verification Activity</p>
        {!data?.hasVerificationActivity ? (
          <div className="text-xs text-muted-foreground bg-muted rounded-md px-3 py-2">
            No external verifications logged yet — this fills in as employers/institutions check a credential via its verify link.
          </div>
        ) : (
          <MiniLineChart data={data?.verificationOverTime} color="var(--chart-3)" />
        )}
      </div>
    </div>
  )
}

export default CredentialOverviewSection